import { useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { previewAutoTransactionEntities } from "../../api/auto-transactions";
import { isClientError } from "../../api/common";
import type { ConditionDto } from "../../api/types";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { ConditionEntityHitsContext, pathKey } from "./conditionEntityHits";

/** Long enough that typing a regex doesn't fire a request per character. */
const DEBOUNCE_MS = 400;

/**
 * Asks the backend what the condition's entity-backed leaves currently name,
 * and hands the answers down by path.
 *
 * The whole tree goes in one debounced request rather than one per leaf. The
 * answer deliberately comes from the server: it is produced by the same
 * matcher the rule engine runs, so what the editor reports and what the rule
 * does on save cannot disagree.
 */
export const ConditionEntityHitsProvider = ({
  condition,
  children,
}: {
  condition: ConditionDto;
  children: ReactNode;
}) => {
  const settled = useDebouncedValue(condition, DEBOUNCE_MS);

  const { data } = useQuery({
    queryKey: ["auto-transactions", "preview-entities", settled],
    queryFn: () => previewAutoTransactionEntities(settled),
    staleTime: 30_000,
    // A refused tree is refused again on retry; each retry is only another
    // console error while the user is mid-edit.
    retry: (failureCount, error) => !isClientError(error) && failureCount < 3,
  });

  const lookup = useMemo(() => {
    const byPath = new Map(
      (data?.conditions ?? []).map((hits) => [pathKey(hits.path), hits]),
    );
    return (path: number[]) => byPath.get(pathKey(path));
  }, [data]);

  return (
    <ConditionEntityHitsContext.Provider value={lookup}>
      {children}
    </ConditionEntityHitsContext.Provider>
  );
};
